import { useContext, useEffect, useRef, useState } from 'react'
import HoldersList from '@/components/HoldersList'
import {
  hasLoadedLaunchHolders,
  loadLaunchHolders,
  snapshotLaunchHolders,
  type LaunchHolder,
} from '@/lib/launchpad/holders'
import { sameTradedLaunch, subscribeLaunchTraded } from '@/lib/launchpad/tradeEvents'
import type { LaunchState } from '@/lib/launchpad/types'
import { WalletAddressContext } from '@/lib/walletContext'

function launchKey(launch: LaunchState, wallet?: string) {
  return [launch.id, launch.contractAddress, launch.tokenAddress, wallet]
    .filter(Boolean)
    .join(':')
    .toLowerCase()
}

function LaunchHolders({ launch }: { launch: LaunchState }) {
  const wallet = useContext(WalletAddressContext)
  const seed = snapshotLaunchHolders(launch)
  const [holders, setHolders] = useState<LaunchHolder[]>(seed)
  const [loading, setLoading] = useState(seed.length === 0)
  const fetchedKey = useRef('')

  useEffect(() => {
    const key = launchKey(launch, wallet)
    let cancelled = false
    const apply = (rows: LaunchHolder[]) => {
      if (cancelled)
        return
      setHolders(rows)
      setLoading(rows.length === 0 && (launch.realQuoteRaised > 0n || launch.tokensSold > 0n))
    }

    const cached = snapshotLaunchHolders(launch)
    if (hasLoadedLaunchHolders(launch) && fetchedKey.current === key && cached.length > 0) {
      apply(cached)
      return () => {
        cancelled = true
      }
    }

    fetchedKey.current = key
    setLoading(cached.length === 0)
    loadLaunchHolders(launch, { wallet })
      .then((rows) => {
        apply(rows)
        const needsRetry = rows.length === 0 && (launch.realQuoteRaised > 0n || launch.tokensSold > 0n)
        if (cancelled || !needsRetry)
          return
        window.setTimeout(() => {
          if (cancelled)
            return
          loadLaunchHolders(launch, { force: true, wallet }).then(apply).catch(() => {})
        }, 1200)
      })
      .catch(() => {
        if (!cancelled)
          setLoading(false)
      })

    const stop = subscribeLaunchTraded((traded) => {
      if (!sameTradedLaunch(traded, launch))
        return
      loadLaunchHolders(traded, { force: true, wallet }).then(apply).catch(() => {})
    })

    return () => {
      cancelled = true
      stop()
    }
  }, [launch.id, launch.contractAddress, launch.tokenAddress, launch.realQuoteRaised, launch.tokensSold, wallet])

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
      <h2 className="text-sm font-semibold text-white">Holders</h2>
      <HoldersList holders={holders} loading={loading} emptyLabel="No holders yet." />
    </div>
  )
}

export default LaunchHolders
